import { useState, useCallback, MouseEvent, useRef } from 'react'
import { ModalOptions, ModalProps, useModal } from './useModal'

/**
 * モーダルを表示する際のCSS Transitionを補助するフック
 * useModalではisOpenに基づいてtransitionを記述すると
 * - 開く際は、transition後のstyleが当てられてしまい
 * - 閉じる際は、transitionが始まる前にモーダルが閉じてしまう
 *
 * このため、isOpenの値にtransition可能な余白を付加している
 * @param duration アニメーションが終わるまでの時間[ms]
 * @returns [モーダルコンポーネント, open, close, モーダルが開いているか(isOpen)]
 */
export const useModalWithTransition = (
  duration: number,
  { elementId, ...modalOptions }: ModalOptions & { elementId?: string } = {}
) => {
  const [BaseModal, openModal, closeModal] = useModal(elementId, modalOptions)
  const [isOpen, setIsOpen] = useState(false)
  const timeoutIdRef = useRef<number>(undefined)

  const open = useCallback(() => {
    openModal()
    window.clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = window.setTimeout(() => setIsOpen(true))
  }, [openModal])
  const close = useCallback(() => {
    setIsOpen(false)
    window.clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = window.setTimeout(closeModal, duration)
  }, [closeModal, duration])

  const onOverlayClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (modalOptions.closeOnOverlayClick ?? true) close()
    },
    [modalOptions.closeOnOverlayClick, close]
  )

  const Modal: React.FC<Partial<ModalProps>> = useCallback(
    (props) => BaseModal({ onOverlayClick, ...props }),
    [BaseModal, onOverlayClick]
  )

  return [Modal, open, close, isOpen] as const
}
